/**
 * @fileoverview Portfolio Dashboard Page
 * @description Shows modules/apps for a specific team (line of business)
 */

import React from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import {
  FileSearch,
  ArrowUpDown,
  PlaneTakeoff,
  ExternalLink,
  Inbox,
  Database,
  GitCompareArrows,
  Workflow,
} from 'lucide-react';
import './PortfolioDashboard.css';

// Define which modules each team has
const PORTFOLIO_CONFIG = {
  jcte: {
    name: 'Jeppesen Crew Tracking Editor',
    shortName: 'JCTE',
    description: 'Crew management, scheduling, and operational data tools for Jeppesen Crew System',
    apps: [
      {
        id: 'jcte-log-analyser',
        name: 'JCTE Log Analyser',
        description: 'AI-powered SFTP log search, data source browsing, and report analytics for crew operations',
        icon: FileSearch,
        path: '/report-logs',
        tag: 'AI-Powered',
      },
      {
        id: 'zamla-report',
        name: 'ZAMLA Report',
        description: 'ZAMLA crew system reporting and analytics for permissions & access management',
        icon: ArrowUpDown,
        path: '/zamla-report',
        tag: 'Analytics',
      },
      {
        id: 'deadhead-pnr',
        name: 'DH PNR Generator',
        description: 'Create and manage Deadhead PNR bookings for crew positioning and travel',
        icon: PlaneTakeoff,
        path: '/deadhead-pnr',
        tag: 'Generator',
      },
      {
        id: 'flight-crew-deadletter',
        name: 'Flight Crew DLQ Search',
        description: 'Search Azure Service Bus dead-letter queue for undelivered flight crew change messages',
        icon: Inbox,
        path: '/flight-crew-deadletter',
        tag: 'Service Bus',
      },
      {
        id: 'servicebus-explorer',
        name: 'Service Bus Explorer',
        description: 'Universal tool to search any Azure Service Bus queue — active, dead-letter, or both — using dynamic key-value criteria',
        icon: Database,
        path: '/servicebus-explorer',
        tag: 'Universal',
      },
      {
        id: 'flight-crew-compare',
        name: 'Flight vs Crew Compare',
        description: 'Compare flights on one Service Bus connection against crew data on another and flag flights that are missing crew',
        icon: GitCompareArrows,
        path: '/flight-crew-compare',
        tag: 'Service Bus',
      },
      {
        id: 'crew-pipeline-check',
        name: 'Crew Pipeline Check',
        description: 'Walk a crew assignment hop by hop across AIMS, Crew on Flight and the crew database to find the stop where it stops matching',
        icon: Workflow,
        path: '/crew-pipeline-check',
        tag: 'Diagnostics',
      },
    ],
  },
  'airport-ops': {
    name: 'Airport Operations',
    shortName: 'Airport Ops',
    description: 'Airport Operations Technology — modules coming soon',
    apps: [],
  },
};

const PortfolioDashboard = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const team = PORTFOLIO_CONFIG[teamId];

  if (!team) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="portfolio-dashboard">
      {/* Hero Header */}
      <div className="portfolio-hero">
        <div className="portfolio-hero-content">
          <span className="portfolio-badge">{team.shortName || team.name}</span>
          <h1>{team.name}</h1>
          <p>{team.description}</p>
        </div>
        <div className="portfolio-hero-stats">
          <div className="hero-stat">
            <span className="hero-stat-value">{team.apps.length}</span>
            <span className="hero-stat-label">Modules</span>
          </div>
        </div>
      </div>

      {/* Apps Grid */}
      {team.apps.length > 0 ? (
        <div className="portfolio-apps-grid">
          {team.apps.map((app) => {
            const Icon = app.icon;
            return (
              <button
                key={app.id}
                className="portfolio-app-card"
                onClick={() => navigate(app.path)}
              >
                <div className="app-card-header">
                  <div className="app-card-icon">
                    <Icon size={24} />
                  </div>
                  {app.tag && <span className="app-card-tag">{app.tag}</span>}
                </div>
                <div className="app-card-content">
                  <h3>{app.name}</h3>
                  <p>{app.description}</p>
                </div>
                <div className="app-card-footer">
                  <span className="app-card-action">
                    Open <ExternalLink size={14} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="portfolio-empty">
          <div className="portfolio-empty-icon">🚀</div>
          <h3>Coming Soon</h3>
          <p>No modules configured for this portfolio yet. Check back later.</p>
        </div>
      )}
    </div>
  );
};

export default PortfolioDashboard;
